import { tv } from 'tailwind-variants'
import { useI18n, type MessageKey } from '../i18n'

/**
 * Status chip for an application's lifecycle state. Modelled from the Figma
 * "status badge" set.
 *
 * Unlike Button/Text (which take arbitrary `children`), this is a *bundled
 * status enum*: the public API is a single `status`, and each status carries
 * both its color and its own translated label — these are domain statuses, not
 * free text. The label is looked up via i18n internally.
 *
 * Two forms, controlled by `container`:
 *   container (default) → filled pill: tinted bg + border + 8px radius + padding
 *   container={false}   → bare text only, in the status color (no pill)
 *
 * Three color tones back the five statuses — the amber/"warning" tone is shared
 * by Under Review, Action Required, and In Progress. The label is stored
 * natural-case in the catalogs and rendered uppercase via CSS.
 */

export type BadgeStatus =
  | 'completed'
  | 'underReview'
  | 'draft'
  | 'actionRequired'
  | 'inProgress'

type Tone = 'success' | 'warning' | 'neutral'

// Each status → its color tone + i18n label key. `MessageKey` typing means a
// missing/misspelled key is a compile error (and strict catalog parity ensures
// every locale provides it).
const STATUS_CONFIG: Record<BadgeStatus, { tone: Tone; labelKey: MessageKey }> = {
  completed: { tone: 'success', labelKey: 'applicationStatus.status.completed' },
  underReview: { tone: 'warning', labelKey: 'applicationStatus.status.underReview' },
  draft: { tone: 'neutral', labelKey: 'applicationStatus.status.draft' },
  actionRequired: { tone: 'warning', labelKey: 'applicationStatus.status.actionRequired' },
  inProgress: { tone: 'warning', labelKey: 'applicationStatus.status.inProgress' },
}

// `tone` sets the text color (applies in both forms); `container` adds the pill
// chrome. The tinted fill + border depend on BOTH, so they live in
// compoundVariants — mirroring Button's disabled×intent pairs. `tone` is derived
// from `status`, never passed by callers.
const badge = tv({
  base: 'inline-flex items-center justify-center font-semibold text-caption uppercase',
  variants: {
    tone: {
      success: 'text-green-700',
      warning: 'text-yellow-900',
      neutral: 'text-gray-700',
    },
    container: {
      true: 'rounded-[8px] border px-3 py-1',
      false: '',
    },
  },
  compoundVariants: [
    { container: true, tone: 'success', class: 'bg-green-50 border-green-200' },
    { container: true, tone: 'warning', class: 'bg-yellow-500 border-yellow-700' },
    { container: true, tone: 'neutral', class: 'bg-gray-200 border-gray-500' },
  ],
  defaultVariants: {
    container: true,
    tone: 'neutral',
  },
})

export type StatusBadgeProps = {
  status: BadgeStatus
  /** Filled pill when true (default); bare text when false. */
  container?: boolean
  className?: string
}

export function StatusBadge({ status, container = true, className }: StatusBadgeProps) {
  const { t } = useI18n()
  const { tone, labelKey } = STATUS_CONFIG[status]
  return <span className={badge({ tone, container, className })}>{t(labelKey)}</span>
}
